// FloatingStandings.tsx - Refactored with shared hooks
import React, { useMemo, useCallback } from 'react';
import { Pin } from 'lucide-react';
import { cn } from '../lib/utils';
import { WinnerWithMetadata } from '../types/types';
import { usePlayerScores } from '../hooks/usePlayerScores';
import { useChangeDetection } from '../hooks/useChangeDetection';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Button } from './ui/button';

interface FloatingStandingsProps {
  playerPicks: string;
  winnersWithMetadata: WinnerWithMetadata[];
  maxPlayers?: number; // Number of top players to show
  pinnedPlayers?: string[]; // Pinned player names from parent
  onPinnedPlayersChange?: (players: string[]) => void;
}

const FloatingStandings: React.FC<FloatingStandingsProps> = ({
  playerPicks,
  winnersWithMetadata,
  maxPlayers = 4,
  pinnedPlayers = [],
  onPinnedPlayersChange
}) => {
  const { playerScores, rankMap } = usePlayerScores(playerPicks, winnersWithMetadata);
  const { changeMap } = useChangeDetection(playerScores);

  const renderRankIndicator = useCallback((playerName: string) => {
    const change = changeMap.get(playerName);
    if (!change || change.rankDelta === 0) return null;
    if (change.rankDelta < 0) {
      return <span className="text-success text-[9px] mr-0.5">&#9650;</span>;
    }
    return <span className="text-destructive text-[9px] mr-0.5">&#9660;</span>;
  }, [changeMap]);

  // Determine which players to show: pinned mode vs default top-N
  const { displayPlayers, leaderPlayer } = useMemo(() => {
    const validPins = pinnedPlayers.filter(name =>
      playerScores.some(p => p.name === name)
    );

    if (validPins.length === 0) {
      return { displayPlayers: playerScores.slice(0, maxPlayers), leaderPlayer: null };
    }

    const pinned = playerScores.filter(p => validPins.includes(p.name));
    const leader = playerScores[0];
    const leaderAlreadyPinned = leader && validPins.includes(leader.name);
    return {
      displayPlayers: pinned,
      leaderPlayer: leaderAlreadyPinned ? null : leader
    };
  }, [playerScores, pinnedPlayers, maxPlayers]);

  const topPlayers = displayPlayers;
  const hasPins = pinnedPlayers.length > 0;

  const handleUnpin = useCallback((playerName: string) => {
    if (!onPinnedPlayersChange) return;
    onPinnedPlayersChange(pinnedPlayers.filter(n => n !== playerName));
  }, [pinnedPlayers, onPinnedPlayersChange]);

  if (topPlayers.length === 0) {
    return (
      <div className="w-full p-2">
        <style>{`
          @keyframes floating-standings-flash {
            0% { background-color: hsl(var(--warning) / 0.15); }
            100% { background-color: transparent; }
          }
          .floating-row-flash {
            animation: floating-standings-flash 2s ease-out;
          }
        `}</style>
        <h4 className="m-0 mb-2 text-sm font-bold text-foreground text-center">Standings</h4>
        <p className="m-0 italic text-muted-foreground text-xs text-center">
          No player data available
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <style>{`
        @keyframes floating-standings-flash {
          0% { background-color: hsl(var(--warning) / 0.15); }
          100% { background-color: transparent; }
        }
        .floating-row-flash {
          animation: floating-standings-flash 2s ease-out;
        }
      `}</style>

      <h4 className="m-0 mb-2 text-sm font-semibold text-foreground text-center">
        {hasPins ? 'Pinned Players' : 'Scenario Standings'}
      </h4>

      <Table className="text-xs table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-6 text-right pr-1 text-xs h-7 px-1">
              #
            </TableHead>
            <TableHead className="text-xs h-7 px-1.5">
              Player
            </TableHead>
            <TableHead className="w-[52px] text-center tabular-nums text-xs h-7 px-1 md:w-11">
              Current
            </TableHead>
            <TableHead className="w-[52px] text-center tabular-nums text-xs h-7 px-1 md:w-11">
              Scenario
            </TableHead>
            <TableHead className="w-4 text-center text-xs h-7 px-0.5">
            </TableHead>
            {hasPins && (
              <TableHead className="w-6 text-center text-xs h-7 px-0.5">
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Leader row at top when pins are active */}
          {leaderPlayer && (
            <TableRow key={`leader-${leaderPlayer.name}`} className="hover:bg-muted/50">
              <TableCell className="text-right pr-1 tabular-nums text-muted-foreground py-1.5 px-1 text-xs">
                {rankMap.get(leaderPlayer.name) || 1}
              </TableCell>
              <TableCell
                className="font-medium text-foreground truncate italic py-1.5 px-1.5 text-xs"
                title={leaderPlayer.name}
              >
                {leaderPlayer.name}
              </TableCell>
              <TableCell className="text-center tabular-nums py-1.5 px-1 text-xs">
                {leaderPlayer.currentScore}
              </TableCell>
              <TableCell className="text-center tabular-nums py-1.5 px-1 text-xs">
                {leaderPlayer.scenarioScore}
              </TableCell>
              <TableCell className="text-center py-1.5 px-0.5 text-xs">
                {renderRankIndicator(leaderPlayer.name)}
              </TableCell>
              <TableCell className="py-1.5 px-0.5 text-xs"></TableCell>
            </TableRow>
          )}
          {/* Pinned players with star, separated from leader by thick border */}
          {topPlayers.map((player, idx) => {
            const change = changeMap.get(player.name);
            const isFlashing = change?.scoreChanged;
            const isFirstPinned = idx === 0 && leaderPlayer;
            return (
              <TableRow
                key={player.name}
                className={cn(
                  "hover:bg-muted/50",
                  isFlashing && "floating-row-flash",
                  isFirstPinned && "border-t-2 border-t-border"
                )}
              >
                <TableCell className="text-right pr-1 tabular-nums text-muted-foreground py-1.5 px-1 text-xs">
                  {rankMap.get(player.name) || ''}
                </TableCell>
                <TableCell
                  className="font-medium text-foreground truncate py-1.5 px-1.5 text-xs"
                  title={player.name}
                >
                  {player.name}
                </TableCell>
                <TableCell className="text-center tabular-nums py-1.5 px-1 text-xs">
                  {player.currentScore}
                </TableCell>
                <TableCell className="text-center tabular-nums py-1.5 px-1 text-xs">
                  {player.scenarioScore}
                </TableCell>
                <TableCell className="text-center py-1.5 px-0.5 text-xs">
                  {renderRankIndicator(player.name)}
                </TableCell>
                {hasPins && (
                  <TableCell className="text-center py-1.5 px-0.5 text-xs">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      onClick={() => handleUnpin(player.name)}
                      title="Unpin player"
                    >
                      <Pin className="h-3 w-3 text-warning fill-warning" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default FloatingStandings;
